import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PRIORITIES } from '@stoplord/shared';
import { tasksApi } from '@/services/tasks';
import type { Task } from '@/services/types';
import { EMPTY, useAuth } from '@/store/auth';
import { toast } from '@/store/ui';
import { useCan, useTerms } from '@/hooks/usePermission';
import { humanize, toLocalInput } from '@/lib/format';
import { Button, Checkbox, Field, Input, Modal, Select, Textarea } from '@/components/ui';
import { MultiUserSelect } from '../shared/UserPicker';
import { compact, handleFormError } from '../shared/forms';

const schema = z.object({
  title: z.string().trim().min(1, 'Give the task a title').max(200),
  description: z.string().max(5000).optional(),
  priority: z.enum(PRIORITIES),
  category: z.string().optional(),
  assigneeIds: z.array(z.string()),
  startDate: z.string().optional(),
  dueAt: z.string().optional(),
  estimatedHours: z.string().optional(),
  requiresApproval: z.boolean(),
  checklist: z.string().optional(),
});
type Form = z.infer<typeof schema>;
const DRAFT = 'stoplord-draft-task';

export function TaskFormModal({ open, onClose, task, related }: { open: boolean; onClose: () => void; task?: Task; related?: { type: 'lead' | 'client' | 'project'; id: string; name: string } }) {
  const terms = useTerms();
  const can = useCan();
  const me = useAuth((s) => s.user?.id ?? '');
  const categories = useAuth((s) => s.tenant?.taskCategories ?? EMPTY);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editing = !!task;
  const { register, handleSubmit, reset, control, formState, setError, watch } = useForm<Form>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!open) return;
    let draft: Partial<Form> = {};
    if (!editing) try { draft = JSON.parse(sessionStorage.getItem(DRAFT) ?? '{}'); } catch { /* ignore */ }
    reset({
      title: task?.title ?? '',
      description: task?.description ?? '',
      priority: task?.priority ?? 'medium',
      category: task?.category ?? '',
      assigneeIds: task?.assigneeIds ?? [me],
      startDate: toLocalInput(task?.startDate, false),
      dueAt: toLocalInput(task?.dueAt),
      estimatedHours: task?.estimatedHours ? String(task.estimatedHours) : '',
      requiresApproval: task?.requiresApproval ?? false,
      checklist: '',
      ...draft,
    });
  }, [open, task, editing, reset, me]);

  useEffect(() => {
    if (editing || !open) return;
    const sub = watch((v) => { try { sessionStorage.setItem(DRAFT, JSON.stringify(v)); } catch { /* ignore */ } });
    return () => sub.unsubscribe();
  }, [watch, editing, open]);

  const mut = useMutation({
    mutationFn: (v: Form) => {
      const base = compact({
        title: v.title,
        description: v.description,
        priority: v.priority,
        category: v.category,
        startDate: v.startDate ? new Date(v.startDate).toISOString() : editing ? null : undefined,
        dueAt: v.dueAt ? new Date(v.dueAt).toISOString() : editing ? null : undefined,
        estimatedHours: v.estimatedHours ? Number(v.estimatedHours) : undefined,
        requiresApproval: v.requiresApproval,
      });
      if (editing) return tasksApi.update(task!._id, base);
      return tasksApi.create({
        ...base,
        assigneeIds: v.assigneeIds,
        checklist: (v.checklist ?? '').split('\n').map((t) => t.trim()).filter(Boolean).map((text) => ({ text, done: false })),
        related: related ? { type: related.type, id: related.id } : undefined,
        tags: [],
      });
    },
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      if (!editing) sessionStorage.removeItem(DRAFT);
      toast.success(editing ? 'Task updated' : 'Task created', t.taskNumber);
      onClose();
      if (!editing) navigate(`/tasks/${t._id}`);
    },
  });

  return (
    <Modal open={open} onClose={onClose} size="lg" title={editing ? `Edit ${task!.taskNumber}` : `New ${terms.task.singular.toLowerCase()}`} description={related ? `Linked to ${related.type} “${related.name}”` : undefined} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={mut.isPending} onClick={handleSubmit((v) => mut.mutateAsync(v).catch((e) => handleFormError(e, setError)))}>{editing ? 'Save changes' : 'Create task'}</Button></>}>
      <form className="grid gap-4 sm:grid-cols-2" noValidate onSubmit={(e) => e.preventDefault()}>
        <Field label="Title" required error={formState.errors.title?.message} className="sm:col-span-2">{(p) => <Input {...p} {...register('title')} />}</Field>
        <Field label="Description" className="sm:col-span-2">{(p) => <Textarea {...p} rows={3} {...register('description')} />}</Field>
        {!editing && (
          <Field label="Assignees" className="sm:col-span-2" hint={can('tasks.assign') ? 'You can assign to anyone in your workspace.' : 'You can only create tasks for yourself.'}>
            {(p) => <Controller control={control} name="assigneeIds" render={({ field }) => (can('tasks.assign') ? <MultiUserSelect id={p.id} value={field.value ?? []} onChange={field.onChange} /> : <p className="text-sm text-ink-muted">Me</p>)} />}
          </Field>
        )}
        <Field label="Priority">{(p) => <Select {...p} {...register('priority')}>{PRIORITIES.map((x) => <option key={x} value={x}>{humanize(x)}</option>)}</Select>}</Field>
        <Field label="Category">{(p) => <Select {...p} {...register('category')}><option value="">None</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</Select>}</Field>
        <Field label="Start date">{(p) => <Input {...p} type="date" {...register('startDate')} />}</Field>
        <Field label="Deadline">{(p) => <Input {...p} type="datetime-local" {...register('dueAt')} />}</Field>
        <Field label="Estimated hours">{(p) => <Input {...p} type="number" min={0} step="0.5" {...register('estimatedHours')} />}</Field>
        <div className="flex items-end pb-2"><Checkbox label="Requires approval when completed" {...register('requiresApproval')} /></div>
        {!editing && <Field label="Checklist" hint="One item per line" className="sm:col-span-2">{(p) => <Textarea {...p} rows={3} {...register('checklist')} />}</Field>}
      </form>
    </Modal>
  );
}
